import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import { DIALOG_CLOSE_BUTTON_SX } from "../../../constants/dialogCloseButtonStyles";
import { withoutBackdropClose } from "../../../utils/withoutBackdropClose";
import {
  AUTO_TAGGING_CONFIRM_CANCEL_BUTTON_SX,
  AUTO_TAGGING_CONFIRM_CONFIRM_BUTTON_SX,
  AUTO_TAGGING_CONFIRM_DIALOG_BODY_SX,
  AUTO_TAGGING_CONFIRM_DIALOG_PAPER_SX,
  AUTO_TAGGING_CONFIRM_DIALOG_TITLE_SX,
} from "../autoTaggingConfirmDialogStyles";

export type TaggingPlanActiveConfirmMode = "activate" | "deactivate";

export type TaggingPlanActiveConfirmCopy = {
  title: string;
  body: string | readonly string[];
};

type TaggingPlanActiveConfirmDialogProps = Readonly<{
  open: boolean;
  mode: TaggingPlanActiveConfirmMode | null;
  saving?: boolean;
  copyByMode?: Record<TaggingPlanActiveConfirmMode, TaggingPlanActiveConfirmCopy>;
  onClose: () => void;
  onConfirm: () => void;
}>;

const TAGGING_PLAN_ACTIVE_COPY: Record<
  TaggingPlanActiveConfirmMode,
  TaggingPlanActiveConfirmCopy
> = {
  activate: {
    title: "Activate this item?",
    body: "Once activated, this item will be included in auto-tagging. When matched, it can be automatically applied as a tag.",
  },
  deactivate: {
    title: "Deactivate this item?",
    body: "Once deactivated, this item will no longer be applied during auto-tagging. Tags that have already been applied will not be removed.",
  },
};

export const ENHANCED_TAGGING_RULE_ACTIVE_COPY: Record<
  TaggingPlanActiveConfirmMode,
  TaggingPlanActiveConfirmCopy
> = {
  activate: {
    title: "Activate this item?",
    body: "After activation, this item can be selected when creating or editing enhanced tagging rules. Related rules will be able to use this item for future auto-tagging.",
  },
  deactivate: {
    title: "Deactivate this item?",
    body: [
      "This item will no longer be available for future enhanced tagging rules. Existing rules using this item may stop working as expected.",
      "Tags that have already been applied will not be removed.",
    ],
  },
};

export default function TaggingPlanActiveConfirmDialog({
  open,
  mode,
  saving = false,
  copyByMode = TAGGING_PLAN_ACTIVE_COPY,
  onClose,
  onConfirm,
}: TaggingPlanActiveConfirmDialogProps) {
  const copy = mode ? copyByMode[mode] : null;
  const bodyParagraphs =
    copy == null ? [] : typeof copy.body === "string" ? [copy.body] : copy.body;

  return (
    <Dialog
      open={open}
      onClose={withoutBackdropClose(onClose)}
      maxWidth={false}
      PaperProps={{ sx: AUTO_TAGGING_CONFIRM_DIALOG_PAPER_SX }}
    >
      <DialogTitle sx={AUTO_TAGGING_CONFIRM_DIALOG_TITLE_SX}>
        {copy?.title}
        <IconButton
          aria-label="Close"
          onClick={onClose}
          disabled={saving}
          sx={DIALOG_CLOSE_BUTTON_SX}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0, pb: 1 }}>
        {bodyParagraphs.map((paragraph, index) => (
          <Typography
            key={paragraph}
            sx={{
              ...AUTO_TAGGING_CONFIRM_DIALOG_BODY_SX,
              ...(index > 0 ? { mt: 1.5 } : {}),
            }}
          >
            {paragraph}
          </Typography>
        ))}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          variant="text"
          onClick={onClose}
          disabled={saving}
          sx={AUTO_TAGGING_CONFIRM_CANCEL_BUTTON_SX}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={saving}
          sx={AUTO_TAGGING_CONFIRM_CONFIRM_BUTTON_SX}
        >
          {saving ? "Confirming…" : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
